/**
 * Break-glass emergency-access controls.
 *
 * - <BreakGlassBanner /> : global sticky banner for staff that shows when
 *   a break-glass window is active (polls `/compliance/break-glass/mine`
 *   every 60 s). Includes a countdown + [End now] button. Renders nothing
 *   for patients or when no session is active.
 *
 * - <BreakGlassButton />: small red inline button to open the start-dialog
 *   from any staff surface (chart modal, orders tab, etc.). Accepts an
 *   optional `patientId` prop so supervisor emails include the patient
 *   context. Rejects non-admin/non-prescriber roles server-side.
 */
import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";

const BREAK_GLASS_ROLES = new Set(["admin", "super_admin", "prescriber"]);

function formatCountdown(isoExpires) {
  if (!isoExpires) return "";
  const ms = new Date(isoExpires).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return hrs > 0 ? `${hrs}h ${rem}m` : `${rem}m`;
}

export function BreakGlassBanner() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [tick, setTick] = useState(0);

  const poll = useCallback(async () => {
    if (!user || !BREAK_GLASS_ROLES.has(user.role)) return;
    try {
      const r = await API.get("/compliance/break-glass/mine");
      setActive(r.data?.active || null);
    } catch (e) {
      // silent — banner is best-effort
    }
  }, [user]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 60000);
    return () => clearInterval(id);
  }, [poll]);

  // 1-minute countdown refresh
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [active]);

  const endNow = async () => {
    if (!active) return;
    try {
      await API.post(`/compliance/break-glass/end/${active.id}`);
      toast.success("Break-glass session ended.");
      setActive(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to end session");
    }
  };

  // Hide banner for non-staff or when no active session
  if (!user || !BREAK_GLASS_ROLES.has(user.role) || !active) return null;

  return (
    <div
      data-testid="break-glass-banner"
      className="sticky top-0 z-40 bg-red-600 text-white shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
        <ShieldAlert className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 text-sm" data-testid="break-glass-banner-text">
          <strong>Break-glass active</strong> — expires in{" "}
          <span data-testid="break-glass-countdown">{formatCountdown(active.expires_at) || `${tick}`}</span>.
          Every PHI access is tagged to this session. Reason: &quot;{active.reason}&quot;
          {active.patient_id ? ` · patient ${active.patient_id.slice(0, 8)}` : ""}
        </div>
        <button
          onClick={endNow}
          data-testid="break-glass-end-btn"
          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 transition flex items-center gap-1.5"
        >
          <X className="w-3.5 h-3.5" /> End now
        </button>
      </div>
    </div>
  );
}

export function BreakGlassButton({ patientId = "", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || !BREAK_GLASS_ROLES.has(user.role)) return null;

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await API.post("/compliance/break-glass/start", {
        reason: reason.trim(),
        patient_id: patientId || null,
      });
      toast.success(
        r.data?.reused
          ? "Existing break-glass session resumed."
          : "Break-glass started — supervisor notified."
      );
      setOpen(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start break-glass");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="break-glass-open-btn"
        className={`border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/30 ${className}`}
      >
        <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
        Break glass
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="break-glass-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Emergency access (break-glass)
            </DialogTitle>
            <DialogDescription>
              This grants you <strong>4 hours</strong> of elevated PHI access. Every
              action during the window is logged and auto-notifies the privacy
              officer. Use only for genuine clinical or operational emergencies.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-800 dark:text-red-300">
              <p className="font-semibold mb-1">Before you continue</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Super-admin receives an email alert immediately.</li>
                <li>Window expires automatically in 4 hours.</li>
                <li>Every PHI read is tagged to this session.</li>
                <li>Inappropriate use is grounds for termination.</li>
              </ul>
            </div>

            <label className="block">
              <span className="text-sm font-medium">
                Clinical / operational reason <span className="text-red-500">*</span>
              </span>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. On-call covering Dr. Smith's panel for patient XYZ presenting at ER with acute hypoglycemia"
                rows={4}
                maxLength={500}
                data-testid="break-glass-reason-input"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                {reason.length}/500 · minimum 10 characters
              </p>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy || reason.trim().length < 10}
              data-testid="break-glass-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy ? "Starting…" : "Activate break-glass"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
